import { useRef } from "react";
import type { JobFileDto } from "@/lib/job-files";

export interface JobFileGridProps {
  jobId: number;
  files: JobFileDto[];
  onUpload: (file: File) => void;
  onUploadNewVersion: (fileId: number, file: File) => void;
  onDelete: (fileId: number) => void;
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function JobFileGrid({ files, onUpload, onUploadNewVersion, onDelete }: JobFileGridProps) {
  const uploadRef = useRef<HTMLInputElement>(null);

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = "";
  }

  function handleUploadNewVersion(fileId: number) {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) onUploadNewVersion(fileId, file);
    };
    input.click();
  }

  return (
    <div className="space-y-3" data-testid="job-file-grid">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Files</h3>
        <label className="cursor-pointer rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90">
          Upload
          <input
            ref={uploadRef}
            type="file"
            className="sr-only"
            aria-label="Upload file"
            onChange={handleUpload}
          />
        </label>
      </div>

      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground">No files attached</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="pb-2 pr-4 font-medium">File name</th>
              <th className="pb-2 pr-4 font-medium">Size</th>
              <th className="pb-2 pr-4 font-medium">Version</th>
              <th className="pb-2 pr-4 font-medium">Uploaded</th>
              <th className="pb-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.jobFileId} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium">{f.originalFileName}</td>
                <td className="py-2 pr-4 text-muted-foreground">{formatBytes(f.currentSizeBytes)}</td>
                <td className="py-2 pr-4 text-muted-foreground">
                  {f.currentVersionSeq != null ? `v${f.currentVersionSeq}` : "—"}
                </td>
                <td className="py-2 pr-4 text-muted-foreground">
                  {f.currentUploadedAtUtc
                    ? new Date(f.currentUploadedAtUtc).toLocaleDateString()
                    : "—"}
                </td>
                <td className="py-2 space-x-2">
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => handleUploadNewVersion(f.jobFileId)}
                  >
                    New version
                  </button>
                  <button
                    type="button"
                    className="text-xs text-destructive hover:underline"
                    onClick={() => onDelete(f.jobFileId)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
